import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { updateIspServer } from '@/api/isp';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
} from './ispSettingsUI';

/**
 * Servers → Edit. Rename a MikroTik, or change the VLAN its subscribers arrive
 * on (Ray, 2026-10-30: "what if they like to replace the vlan?").
 *
 * The VLAN can only be moved on a router SmartFi built — someone else's PPPoE
 * setup is theirs to change in Winbox, and the field says so instead of
 * pretending.
 */
interface Props {
   server: {
      id: string;
      name: string;
      subscriberVlan?: number;
      smartfiBuilt?: boolean;
   };
   onCancel: () => void;
   onSaved: () => void;
}

const IspServerEdit: React.FC<Props> = ({ server, onCancel, onSaved }) => {
   const [name, setName] = useState(server.name);
   const [vlan, setVlan] = useState(
      server.subscriberVlan ? String(server.subscriberVlan) : ''
   );
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState('');

   const before = server.subscriberVlan ?? 0;
   const vlanNum = vlan.trim() === '' ? 0 : Number(vlan.trim());
   const vlanChanged = vlanNum !== before;

   const save = async () => {
      if (name.trim().length < 2) {
         setError('Give this router a name.');
         return;
      }
      if (!Number.isInteger(vlanNum) || vlanNum === 1 || vlanNum < 0 || vlanNum > 4094) {
         setError('VLAN number must be from 2 to 4094 — or leave it empty.');
         return;
      }
      setError('');
      setBusy(true);
      try {
         await updateIspServer(server.id, {
            name: name.trim(),
            ...(vlanChanged ? { subscriberVlan: vlanNum } : {}),
         });
         toast.success(vlanChanged ? 'Saved — the router now uses the new VLAN.' : 'Saved.');
         onSaved();
      } catch (e: unknown) {
         setError(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save. Try again.'
         );
      } finally {
         setBusy(false);
      }
   };

   return (
      <Wrap>
         <Grid>
            <SettingsField>
               <SettingsLbl htmlFor="srv-name">Name</SettingsLbl>
               <SettingsInput
                  id="srv-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
               />
            </SettingsField>
            <SettingsField>
               <SettingsLbl htmlFor="srv-vlan">VLAN number</SettingsLbl>
               <SettingsInput
                  id="srv-vlan"
                  type="text"
                  inputMode="numeric"
                  value={vlan}
                  disabled={!server.smartfiBuilt}
                  placeholder="Empty = not on a VLAN"
                  onChange={(e) => setVlan(e.target.value.replace(/\D/g, ''))}
               />
               <SettingsHintSm>
                  {server.smartfiBuilt
                     ? 'The VLAN your OLT sends subscribers on.'
                     : 'This PPPoE server is your own setup — change its VLAN in Winbox.'}
               </SettingsHintSm>
            </SettingsField>
         </Grid>

         {vlanChanged && server.smartfiBuilt && (
            <Warn>
               Changing the VLAN drops every connected customer once — their
               modems redial on the new VLAN within a minute. Set the same VLAN
               on your OLT first.
            </Warn>
         )}
         {error && <Err>{error}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
            <Primary type="button" disabled={busy} onClick={save}>
               {busy ? 'Saving…' : 'Save'}
            </Primary>
         </Actions>
      </Wrap>
   );
};

export default IspServerEdit;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   min-width: min(520px, 100%);
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px;

   @media (max-width: 560px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Warn = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   background: #fff7e6;
   color: #8a5a00;
   font-size: 13px;
`;

const Err = styled.div`
   color: ${colors.DANGER};
   font-size: 13px;
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;

const Primary = styled.button`
   padding: 9px 18px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-weight: 700;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
   }
`;

const Ghost = styled.button`
   padding: 9px 16px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: ${colors.MAIN_DARK};
   font-weight: 600;
   cursor: pointer;
`;
